import * as React from 'react';

import type { MoneyField } from '@insify/global-money';

/** Should move these typings to an external package, not sure what package this should be */
export type PastClaim<CoverageKey extends string> = {
  id: string;
  year: string;
  coverage: CoverageKey;
  amount: MoneyField;
};

export type PastClaimsAction<CoverageKey extends string> =
  | {
      type: 'ADD_CLAIM';
      payload: PastClaim<CoverageKey>;
    }
  | {
      type: 'REMOVE_CLAIM';
      payload: PastClaim<CoverageKey>;
    }
  | {
      type: 'UPDATE_CLAIM';
      payload: PastClaim<CoverageKey>;
    }
  | {
      type: 'OPEN_PAST_CLAIM_FORM';
      payload?: PastClaim<CoverageKey>;
    }
  | {
      type: 'CLOSE_PAST_CLAIM_FORM';
    }
  | {
      type: 'SET_HAS_PAST_CLAIMS';
      payload: boolean;
    }
  | {
      type: 'PROP_UPDATE_PAST_CLAIMS';
      payload: PastClaim<CoverageKey>[];
    }
  | {
      type: 'PROP_UPDATE_HAS_PAST_CLAIMS';
      payload: boolean;
    };

type CoverageOption = { value: string; label: string };

export type PastClaimsState<CoverageKey extends string> = {
  pastClaims: PastClaim<CoverageKey>[];
  pastClaimFormState?: Partial<PastClaim<CoverageKey>>;
  pastClaimThreshold?: MoneyField;
  pastClaimsLookbackYears?: number;
  coverageOptions: CoverageOption[];
  hasPastClaimFormEnabled: boolean;
  hasPastClaims: boolean | undefined;
};

export type PastClaimsDispatch<CoverageKey extends string> = {
  addClaim: (claim: PastClaim<CoverageKey>) => void;
  removeClaim: (claim: PastClaim<CoverageKey>) => void;
  updateClaim: (claim: PastClaim<CoverageKey>) => void;
  openPastClaimForm: (claimToEdit?: PastClaim<CoverageKey>) => void;
  closePastClaimForm: () => void;
  setHasPastClaims: (hasPastClaims: boolean) => void;
  submit: (answer: PastClaim<CoverageKey>[]) => void;
};

function pastClaimsReducer<CoverageKey extends string>(
  state: PastClaimsState<CoverageKey>,
  action: PastClaimsAction<CoverageKey>,
): PastClaimsState<CoverageKey> {
  switch (action.type) {
    case 'ADD_CLAIM':
      return { ...state, pastClaims: [...state.pastClaims, action.payload] };
    case 'REMOVE_CLAIM':
      return { ...state, pastClaims: state.pastClaims.filter((claim) => claim.id !== action.payload.id) };
    case 'UPDATE_CLAIM': {
      const claimIndex = state.pastClaims.findIndex((claim) => claim.id === action.payload.id);
      const updatedClaims = [...state.pastClaims];
      updatedClaims.splice(claimIndex, 1, action.payload);
      return { ...state, pastClaims: updatedClaims };
    }
    case 'OPEN_PAST_CLAIM_FORM': {
      return { ...state, pastClaimFormState: action.payload, hasPastClaimFormEnabled: true };
    }
    case 'CLOSE_PAST_CLAIM_FORM': {
      return { ...state, hasPastClaimFormEnabled: false };
    }
    case 'SET_HAS_PAST_CLAIMS': {
      const hasPastClaims = action.payload;
      return {
        ...state,
        pastClaims: hasPastClaims ? state.pastClaims : [],
        hasPastClaims,
      };
    }
    case 'PROP_UPDATE_PAST_CLAIMS': {
      return { ...state, pastClaims: action.payload };
    }
    case 'PROP_UPDATE_HAS_PAST_CLAIMS': {
      return { ...state, hasPastClaims: action.payload };
    }
    default:
      throw new Error();
  }
}
const initialState = {
  pastClaims: [],
  coverageOptions: [],
  hasPastClaimFormEnabled: false,
  hasPastClaims: undefined,
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const contextClaimsState = React.createContext<PastClaimsState<any> | undefined>(undefined);
const contextClaimsDispatch = React.createContext<PastClaimsDispatch<any> | undefined>(undefined);

export type PastClaimsProviderProps<CoverageKey extends string> = {
  hasPastClaims?: boolean;
  pastClaims: PastClaim<CoverageKey>[];
  coverageOptions: CoverageOption[];
  pastClaimThreshold?: MoneyField;
  pastClaimsLookbackYears?: number;
  eventHandlers: {
    onAdd?: (pastClaim: PastClaim<CoverageKey>) => void;
    onRemove?: (pastClaim: PastClaim<CoverageKey>) => void;
    onUpdate?: (pastClaim: PastClaim<CoverageKey>) => void;
    onSubmit?: (pastClaims: PastClaim<CoverageKey>[]) => void;
  };
  children?: React.ReactNode;
};

export const PastClaimsProvider = <CoverageKey extends string>({
  pastClaims,
  hasPastClaims,
  coverageOptions,
  pastClaimThreshold,
  pastClaimsLookbackYears,
  children,
  eventHandlers,
}: PastClaimsProviderProps<CoverageKey>) => {
  const [state, dispatchPastClaims] = React.useReducer(pastClaimsReducer, initialState);

  const setHasPastClaims = (hasPastClaims: boolean) => {
    dispatchPastClaims({ type: 'SET_HAS_PAST_CLAIMS', payload: hasPastClaims });
  };

  const openPastClaimForm = (pastClaimToEdit?: PastClaim<CoverageKey>) => {
    dispatchPastClaims({ type: 'OPEN_PAST_CLAIM_FORM', payload: pastClaimToEdit });
  };

  const closePastClaimForm = () => {
    dispatchPastClaims({ type: 'CLOSE_PAST_CLAIM_FORM' });
  };

  const addClaim = (claim: PastClaim<CoverageKey>) => {
    dispatchPastClaims({ type: 'ADD_CLAIM', payload: claim });
    eventHandlers.onAdd?.(claim);
  };

  const removeClaim = (removedClaim: PastClaim<CoverageKey>) => {
    dispatchPastClaims({ type: 'REMOVE_CLAIM', payload: removedClaim });
    eventHandlers.onRemove?.(removedClaim);
  };

  const updateClaim = (updatedClaim: PastClaim<CoverageKey>) => {
    dispatchPastClaims({ type: 'UPDATE_CLAIM', payload: updatedClaim });
    eventHandlers.onUpdate?.(updatedClaim);
  };

  const submit = (pastClaims: PastClaim<CoverageKey>[]) => {
    eventHandlers.onSubmit?.(pastClaims);
  };

  React.useEffect(() => {
    if (pastClaims) {
      dispatchPastClaims({ type: 'PROP_UPDATE_PAST_CLAIMS', payload: pastClaims });
    }
  }, [pastClaims]);

  React.useEffect(() => {
    if (typeof hasPastClaims !== 'undefined') {
      dispatchPastClaims({ type: 'PROP_UPDATE_HAS_PAST_CLAIMS', payload: hasPastClaims });
    }
  }, [hasPastClaims]);

  return (
    <contextClaimsDispatch.Provider
      value={{
        submit,
        addClaim,
        removeClaim,
        updateClaim,
        openPastClaimForm,
        closePastClaimForm,
        setHasPastClaims,
      }}
    >
      <contextClaimsState.Provider value={{ ...state, coverageOptions, pastClaimThreshold, pastClaimsLookbackYears }}>
        {children}
      </contextClaimsState.Provider>
    </contextClaimsDispatch.Provider>
  );
};

export function usePastClaimsState<CoverageKey extends string>(): PastClaimsState<CoverageKey> {
  const context = React.useContext(contextClaimsState);
  if (context === undefined) {
    throw new Error('useContextClaims must be used within a ContextClaims');
  }
  return context;
}

export function usePastClaimsDispatch<CoverageKey extends string>(): PastClaimsDispatch<CoverageKey> {
  const context = React.useContext(contextClaimsDispatch);
  if (context === undefined) {
    throw new Error('useClaimUpdates must be used within a ContextClaims');
  }
  return context;
}

export const usePastClaims = <CoverageKey extends string>() => {
  return [usePastClaimsState<CoverageKey>(), usePastClaimsDispatch<CoverageKey>()] as const;
};
